import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';

interface ImportHistoryItem {
  id: number;
  usuario_nome: string;
  usuario_email: string;
  data_inicio_filtro: string;
  data_fim_filtro: string;
  total_registros_api: number;
  registros_importados: number;
  registros_atualizados: number;
  registros_com_erro: number;
  status: string;
  detalhes_erro?: string;
  tempo_processamento: number;
  criado_em: string;
  finalizado_em?: string;
}

const ImportHistory: React.FC = () => {
  const navigate = useNavigate();
  const [selectedImport, setSelectedImport] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Hook de infinite scroll
  const {
    items: imports,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<ImportHistoryItem>({
    fetchFunction: async (page, limit) => {
      try {
        const response: any = await apiService.get('FUELING_IMPORTS', {}, { page, limit });

        if (response.success && response.data) {
          const importacoes = response.data.importacoes || response.data;
          const pagination = response.data.paginacao || response.data.pagination;

          return {
            success: true,
            data: {
              items: importacoes,
              pagination: {
                total: pagination?.total || importacoes.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error: any) {
        console.error('Erro ao carregar histórico:', error);
        return {
          success: false,
          data: [],
          message: error.message || 'Erro ao carregar histórico de importações'
        };
      }
    },
    itemsPerPage: 10,
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const viewDetails = async (id: number) => {
    try {
      const response: any = await apiService.get('FUELING_IMPORT_BY_ID', { id });

      if (response.success && response.data) {
        setSelectedImport(response.data);
        setShowDetailsModal(true);
      }
    } catch (error: any) {
      console.error('Erro ao carregar detalhes:', error);
      utils.showNotification(error.message || 'Erro ao carregar detalhes da importação', { type: 'error' });
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR');
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      'em_andamento': { label: 'Em andamento', class: 'status-badge status-info' },
      'concluido': { label: 'Concluído', class: 'status-badge status-success' },
      'concluido_com_erros': { label: 'Concluído com erros', class: 'status-badge status-warning' },
      'erro': { label: 'Erro', class: 'status-badge status-danger' }
    };

    const statusInfo = statusMap[status] || { label: status, class: 'status-badge status-secondary' };
    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Histórico de Importações CTA Smart</h1>
          <p style={{ color: '#7f8c8d', marginTop: '8px' }}>
            Visualize o histórico completo de importações de abastecimentos da CTA Smart
          </p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/abastecimentos')}
        >
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      </div>

      <div className="page-content">
        {isLoading && imports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '100px 20px' }}>
            <div className="loading-spinner-large"></div>
            <p>Carregando histórico...</p>
          </div>
        ) : imports.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-history empty-state-icon"></i>
            <h3>Nenhuma importação encontrada</h3>
            <p>Não há histórico de importações de abastecimentos da CTA Smart.</p>
          </div>
        ) : (
          <div className="card">
            <div className="card-header">
              <h2>Registro de Importações</h2>
            </div>
            <div className="card-body">
              {/* Contador de resultados */}
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="importações"
              />

              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Data/Hora</th>
                      <th>Usuário</th>
                      <th>Período</th>
                      <th>Status</th>
                      <th>Novos</th>
                      <th>Atualizados</th>
                      <th>Erros</th>
                      <th>Total API</th>
                      <th>Tempo</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imports.map((imp) => (
                      <tr key={imp.id}>
                        <td><strong>{imp.id}</strong></td>
                        <td>
                          <div style={{ fontSize: '0.9rem' }}>
                            {formatDate(imp.criado_em)}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.9rem' }}>{imp.usuario_nome}</div>
                          <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{imp.usuario_email}</div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>
                            {new Date(imp.data_inicio_filtro).toLocaleDateString('pt-BR')} até{' '}
                            {new Date(imp.data_fim_filtro).toLocaleDateString('pt-BR')}
                          </div>
                        </td>
                        <td>{getStatusBadge(imp.status)}</td>
                        <td>
                          <span className="status-badge status-success" style={{ fontSize: '0.85rem' }}>
                            {imp.registros_importados || 0}
                          </span>
                        </td>
                        <td>
                          <span className="status-badge status-warning" style={{ fontSize: '0.85rem' }}>
                            {imp.registros_atualizados || 0}
                          </span>
                        </td>
                        <td>
                          {imp.registros_com_erro > 0 ? (
                            <span className="status-badge status-danger" style={{ fontSize: '0.85rem' }}>
                              {imp.registros_com_erro}
                            </span>
                          ) : (
                            <span style={{ color: '#95a5a6' }}>-</span>
                          )}
                        </td>
                        <td>
                          <span className="status-badge status-info" style={{ fontSize: '0.85rem' }}>
                            {imp.total_registros_api || 0}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>{imp.tempo_processamento || 0}s</div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => viewDetails(imp.id)}
                              title="Ver detalhes"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Botão de carregar mais */}
              <LoadMoreButton
                onClick={loadMore}
                isLoading={isLoading}
                hasMore={hasMore}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedImport && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-info-circle"></i> Detalhes da Importação #{selectedImport.id}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong style={{ color: '#2c3e50' }}>Status:</strong>
                    <div style={{ marginTop: '5px' }}>{getStatusBadge(selectedImport.status)}</div>
                  </div>
                  <div className="col-md-6">
                    <strong style={{ color: '#2c3e50' }}>Usuário:</strong>
                    <div style={{ marginTop: '5px', color: '#2c3e50' }}>{selectedImport.usuario_nome}</div>
                    <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>{selectedImport.usuario_email}</div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong style={{ color: '#2c3e50' }}>Iniciado em:</strong>
                    <div style={{ marginTop: '5px', color: '#2c3e50' }}>{formatDate(selectedImport.criado_em)}</div>
                  </div>
                  <div className="col-md-6">
                    <strong style={{ color: '#2c3e50' }}>Finalizado em:</strong>
                    <div style={{ marginTop: '5px', color: '#2c3e50' }}>{formatDate(selectedImport.finalizado_em)}</div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-12">
                    <strong style={{ color: '#2c3e50' }}>Período filtrado:</strong>
                    <div style={{ marginTop: '5px', color: '#2c3e50' }}>
                      {new Date(selectedImport.data_inicio_filtro).toLocaleDateString('pt-BR')} até{' '}
                      {new Date(selectedImport.data_fim_filtro).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-3">
                    <div style={{ padding: '20px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center', border: '1px solid #81c784' }}>
                      <h3 style={{ color: '#2e7d32', marginBottom: '8px' }}>{selectedImport.registros_importados || 0}</h3>
                      <div style={{ fontSize: '0.85rem', color: '#2e7d32', fontWeight: '600' }}>Novos</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div style={{ padding: '20px', background: '#fff3e0', borderRadius: '8px', textAlign: 'center', border: '1px solid #ffb74d' }}>
                      <h3 style={{ color: '#e65100', marginBottom: '8px' }}>{selectedImport.registros_atualizados || 0}</h3>
                      <div style={{ fontSize: '0.85rem', color: '#e65100', fontWeight: '600' }}>Atualizados</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div style={{ padding: '20px', background: '#ffebee', borderRadius: '8px', textAlign: 'center', border: '1px solid #e57373' }}>
                      <h3 style={{ color: '#c62828', marginBottom: '8px' }}>{selectedImport.registros_com_erro || 0}</h3>
                      <div style={{ fontSize: '0.85rem', color: '#c62828', fontWeight: '600' }}>Erros</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div style={{ padding: '20px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center', border: '1px solid #64b5f6' }}>
                      <h3 style={{ color: '#1565c0', marginBottom: '8px' }}>{selectedImport.total_registros_api || 0}</h3>
                      <div style={{ fontSize: '0.85rem', color: '#1565c0', fontWeight: '600' }}>Total API</div>
                    </div>
                  </div>
                </div>

                {selectedImport.detalhes_erro && (
                  <div style={{ padding: '15px', background: '#ffebee', borderRadius: '4px', border: '1px solid #e57373', marginBottom: '15px' }}>
                    <strong style={{ color: '#c62828' }}>Detalhes do Erro:</strong>
                    <p style={{ marginBottom: 0, marginTop: '8px', color: '#2c3e50' }}>{selectedImport.detalhes_erro}</p>
                  </div>
                )}

                {selectedImport.erros && selectedImport.erros.length > 0 && (
                  <div className="mt-3">
                    <strong style={{ color: '#2c3e50' }}>Erros de Importação ({selectedImport.erros.length}):</strong>
                    <div className="mt-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {selectedImport.erros.map((erro: any, index: number) => (
                        <div key={index} style={{ padding: '12px', background: '#fff3e0', borderRadius: '4px', border: '1px solid #ffb74d', marginBottom: '8px' }}>
                          <div style={{ color: '#2c3e50' }}><strong>CTA Smart ID:</strong> {erro.cta_smart_id}</div>
                          <div style={{ color: '#2c3e50' }}><strong>Tipo:</strong> {erro.tipo_erro}</div>
                          <div style={{ color: '#2c3e50' }}><strong>Mensagem:</strong> {erro.mensagem_erro}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedImport.veiculos_nao_encontrados && selectedImport.veiculos_nao_encontrados.length > 0 && (
                  <div className="mt-3">
                    <strong style={{ color: '#2c3e50' }}>Veículos Não Encontrados ({selectedImport.veiculos_nao_encontrados.length}):</strong>
                    <div className="mt-2">
                      {selectedImport.veiculos_nao_encontrados.map((veiculo: any, index: number) => (
                        <div key={index} style={{ padding: '12px', background: '#e3f2fd', borderRadius: '4px', border: '1px solid #64b5f6', marginBottom: '8px' }}>
                          <div style={{ color: '#2c3e50' }}>
                            <strong>Placa:</strong> {veiculo.placa} -{' '}
                            <span>{veiculo.total} abastecimento(s)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportHistory;
